import React, { FC, useContext } from 'react';
import { ThemeProvider } from '@mui/material';
import useMediaQuery from '../hooks/useMediaQuery';
import LayoutContext from '../context/Layout';
import useLayoutLinks from './hooks/useLayoutLinks';
import useTranslations from './hooks/useTranslations';
import useHeaderTheme from './hooks/useHeaderTheme';
import ImpersonationBanner from '../ImpersonationBanner';
import useAnalytics from './hooks/useAnalytics';
import { HeaderContainer } from './styles';
import { HeaderProps, BankDataWithPointName } from './types';
import VariableHeader from './VariableHeader';
import MobileContainer from './MobileContainer';
import { BankProvider } from './BankContext';

const Header: FC<HeaderProps> = ({
  fullStoryOrg,
  isImpersonating,
  specialPointName,
  onStopImpersonation,
  routerPath,
  routerPush,
  routerRoutes = [],
  referrerUrl,
  variant = 'normal',
  logoutUrl,
  bankData,
  ...props
}) => {
  const headerTheme = useHeaderTheme();
  const isMobile = useMediaQuery('@media (max-width: 768px)');
  const [currentIdentityQuery, layoutQuery, localeQuery] =
    useContext(LayoutContext);
  const currentIdentity = currentIdentityQuery?.data?.currentIdentity;
  const links = useLayoutLinks(layoutQuery?.data);
  useTranslations(
    currentIdentityQuery?.data?.currentIdentity,
    localeQuery?.data?.customerCoreLocaleMapsByCustomer?.nodes,
  );
  const testRoute = (url: string) => (route: string) =>
    route.length > 1 ? url.startsWith(route) : url === route;

  const layout = layoutQuery?.data;
  const headerView = variant === 'normal' ? true : false;

  const bankDataWithPointName: BankDataWithPointName = {
    ...bankData,
    suppliedSpecialPointName: specialPointName,
    vibesPointName:
      layout?.vibeGetDerivedVibeDetails?.attributes?.text?.pointsNamePlural,
  };

  useAnalytics({
    currentIdentity,
    fullStoryOrg,
    links,
    bank: bankData.bank,
  });

  return (
    <ThemeProvider theme={headerTheme}>
      <BankProvider bankData={bankDataWithPointName}>
        {currentIdentity && isImpersonating && (
          <ImpersonationBanner
            identity={currentIdentity}
            onStopImpersonation={onStopImpersonation}
          />
        )}
        <HeaderContainer {...props}>
          {isMobile ? (
            <MobileContainer
              routerObj={{ routerPush, routerRoutes, testRoute }}
              logoutUrl={logoutUrl}
            />
          ) : (
            <VariableHeader
              headerView={headerView}
              logoutUrl={logoutUrl}
              routerObj={{ routerPush, routerRoutes, routerPath, testRoute }}
            />
          )}
        </HeaderContainer>
      </BankProvider>
    </ThemeProvider>
  );
};

export default Header;
